import { CanvasMark } from '../plane/CanvasMark';
import type { ButtonHTMLAttributes, CSSProperties, MouseEvent, ReactNode } from 'react';
import { dayLabel } from '../design/dates';
import { CardTagMark } from '../folders/TagMark';
import type { TagMark } from '../folders/TagMark';
import { StatusGlyph } from '../board/StatusGlyph';
import { timeOf } from './corpus';
import type { CorpusItem } from './corpus';
import { durationLabel } from './duration';
import { topStatus } from './gathering';
import type { GatheredIn } from './gathering';
import type { Thought } from './thought';

/**
 * A capture as a card. Its head says what it is — a recording's player and
 * how long it runs, or a note's glyph — then its tag's mark and the dot that
 * says it feeds an arc, in that arc's state (the liveliest one it is in; WHICH
 * arcs is a question for its own page). Then its name, when someone gave it
 * one, and its face — every picked line, since a card has no line budget to
 * keep; a thought with no face shows the first thing said in it instead. Its
 * foot says when.
 *
 * [fill] is what the card wears, from its thought's first tag. [mark] shows
 * only where what is around the card does not already say the tag, and
 * [dayShown] is set where a head above already names the day, so the foot
 * gives the time alone.
 *
 * The player is not a control: the card is one button, and a second inside it
 * would be a target within a target. Play is on the recording's page.
 *
 * Minimized, it is one row: the player and length (or the note glyph), its
 * name where it has one, the mark and the dot, and a way to open it back up —
 * no face and no foot. Expanded, the foot's far corner holds ⋮ where the
 * place it sits in has something to offer.
 *
 * With controls of its own the card can no longer be one button: it becomes a
 * surface with an open target under everything else, and each control a
 * target of its own above it. [shell] holds that surface in an element of the
 * caller's own — a canvas card that is also dragged, linked and removed —
 * and [openProps] reach its open target.
 */
export function ThoughtCard({
  item,
  thought,
  gatheredIn,
  fill,
  mark,
  dayShown = false,
  onCanvas = false,
  focused,
  selected = false,
  minimized = false,
  onExpand,
  onMore,
  shell,
  openProps,
  onOpen,
}: {
  item: CorpusItem;
  thought: Thought | undefined;
  gatheredIn: GatheredIn[];
  fill?: string;
  mark?: TagMark | null;
  dayShown?: boolean;
  /** Already on the canvas the page draws: the app's mark after the status. */
  onCanvas?: boolean;
  focused?: boolean;
  /** Gathered into the page's selection. */
  selected?: boolean;
  minimized?: boolean;
  /** Offered while minimized, at the row's end. */
  onExpand?: () => void;
  /** Offered while expanded, as ⋮ at the foot's far corner; told where it was
   *  pressed so a menu can open there. */
  onMore?: (at: { x: number; y: number }) => void;
  shell?: (card: CardShell) => ReactNode;
  openProps?: ButtonHTMLAttributes<HTMLButtonElement>;
  onOpen: (event: MouseEvent) => void;
}) {
  const status = topStatus(gatheredIn);
  const recording = item.kind === 'recording';
  const when = stampOf(item, dayShown);
  const more = !minimized && onMore;
  const foot = !minimized && (when || mark || more);
  const className =
    `thcard ${recording ? 'is-recording' : 'is-note'}` +
    `${minimized ? ' is-minimized' : foot ? '' : ' is-footless'}`;
  const style: CSSProperties = fill ? { background: fill } : {};
  const line = minimized ? rowLine(thought, recording) : null;

  const content = (
    <>
      <span className="thcard-head">
        {recording ? <Player durationMs={thought?.durationMs ?? null} /> : <NoteGlyph />}
        {line ? <span className="thcard-line">{line}</span> : <span className="thcard-space" />}
        {status && (
          <span className="thcard-status" title={`in an arc that is ${status}`}>
            <StatusGlyph status={status} size={11} />
          </span>
        )}
        {onCanvas && <CanvasMark />}
        {minimized && mark && <CardTagMark mark={mark} size={13} />}
        {minimized && onExpand && (
          <button type="button" className="thcard-control" aria-label="Expand" title="Expand" onClick={onExpand}>
            <ExpandGlyph />
          </button>
        )}
      </span>
      {!minimized && <Body thought={thought} />}
      {foot && (
        <span className="thcard-foot">
          {when}
          {mark && <CardTagMark mark={mark} size={12} />}
          {more && (
            <button
              type="button"
              className="thcard-control thcard-more"
              aria-label="More"
              title="More"
              onClick={(e) => {
                const at = e.currentTarget.getBoundingClientRect();
                more({ x: at.left, y: at.bottom + 4 });
              }}
            >
              <MoreGlyph />
            </button>
          )}
        </span>
      )}
    </>
  );

  if (!shell && !onExpand && !onMore) {
    return (
      <button
        type="button"
        className={`${className}${focused ? ' focused' : ''}${selected ? ' selected' : ''}`}
        data-focused={focused || undefined}
        style={fill ? style : undefined}
        onClick={onOpen}
      >
        {content}
      </button>
    );
  }

  const surface: CardShell = {
    className,
    style,
    content: (
      <>
        <button
          type="button"
          className="thcard-open"
          aria-label={openLabel(thought, recording)}
          onClick={onOpen}
          {...openProps}
        />
        {content}
      </>
    ),
  };
  if (shell) return <>{shell(surface)}</>;
  return (
    <div className={`${className}${focused ? ' focused' : ''}${selected ? ' selected' : ''}`} data-focused={focused || undefined} style={style}>
      {surface.content}
    </div>
  );
}

/** The card's own classes, fill and insides — its open target first — for a
 *  caller that holds them in its own element. */
export interface CardShell {
  className: string;
  style: CSSProperties;
  content: ReactNode;
}

/** What one row says besides the player: the name someone gave it, or for a
 *  note, which has no player to go by, its first line. */
function rowLine(thought: Thought | undefined, recording: boolean): string | null {
  if (!thought) return null;
  if (thought.title) return thought.title;
  if (recording) return null;
  return thought.face[0] ?? (thought.standIn || null);
}

function openLabel(thought: Thought | undefined, recording: boolean): string {
  const name = thought?.title ?? thought?.face[0] ?? thought?.standIn;
  return `Open ${recording ? 'recording' : 'note'}${name ? `: ${name}` : ''}`;
}

/** When it was captured: the time alone where the day is said above it,
 *  `sat 26 sep · 20:58` anywhere else, and nothing for an undated one. */
export function stampOf(item: CorpusItem, dayShown: boolean): string {
  if (!item.at) return '';
  return dayShown ? timeOf(item) : `${dayLabel(item.at)} · ${timeOf(item)}`;
}

function Body({ thought }: { thought: Thought | undefined }) {
  if (!thought) {
    return (
      <span className="th-unread" aria-label="reading">
        <span style={{ width: '72%' }} />
        <span style={{ width: '43%' }} />
      </span>
    );
  }

  const words = thought.face.length > 0 ? thought.face : thought.standIn ? [thought.standIn] : [];
  if (!thought.title && words.length === 0) {
    return <span className="thcard-words is-empty">nothing in this one yet</span>;
  }

  return (
    <>
      {thought.title && <span className="thcard-title">{thought.title}</span>}
      {words.map((line, i) => (
        <span key={i} className="thcard-words">
          {line}
        </span>
      ))}
    </>
  );
}

/** A recording's player, and how long it runs where that is known. */
function Player({ durationMs }: { durationMs: number | null }) {
  return (
    <>
      <span className="thcard-play" aria-label="recording">
        <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
          <path d="M8 5v14l11-7z" />
        </svg>
      </span>
      {durationMs !== null && <span className="thcard-duration">{durationLabel(durationMs)}</span>}
    </>
  );
}

function NoteGlyph() {
  return (
    <span className="thcard-kind" aria-label="note">
      <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" aria-hidden="true">
        <path d="M5 7h14M5 12h14M5 17h9" />
      </svg>
    </span>
  );
}

function ExpandGlyph() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M6 9l6 6 6-6" />
    </svg>
  );
}

function MoreGlyph() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
      <circle cx="12" cy="5" r="1.7" />
      <circle cx="12" cy="12" r="1.7" />
      <circle cx="12" cy="19" r="1.7" />
    </svg>
  );
}
